// src/pages/ArchiveView.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import BBSHeader from '../components/BBS/BBSHeader';
import BBSNav from '../components/BBS/BBSNav';
import StatusBar from '../components/BBS/StatusBar';
// CHANGED: same imports you already use, just the updated versions
import { setPageMeta, setStorySchema } from '../utils/seo';

marked.setOptions({ breaks: true, gfm: true });

export default function ArchiveView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setTranscript(null);

    fetch(`/api/stories/${slug}`)
      .then(res => {
        if (!res.ok) throw new Error('Transcript not found');
        return res.json();
      })
      .then(data => {
        setTranscript(data);
        setLoading(false);

        // CHANGED: setPageMeta now also sets canonical, og:image, twitter:card
        setPageMeta({
          title: data.title,
          description: data.excerpt,
          url: `https://thegodcircuit.com/archives/${data.slug}`,
          type: 'article',
        });
        // CHANGED: setStorySchema now uses Article type with mainEntityOfPage
        setStorySchema(data);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });

    return () => {
      setStorySchema(null);
      // CHANGED: reset to defaults on unmount
      setPageMeta();
    };
  }, [slug]);

  function formatDate(dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  if (loading) {
    return (
      <div>
        <BBSHeader title="RETRIEVING TRANSMISSION" />
        <BBSNav />
        <div className="bbs-loading">
          DECODING ARCHIVED SIGNAL
          <span className="bbs-loading-dots" />
        </div>
        <StatusBar />
      </div>
    );
  }

  if (error || !transcript) {
    return (
      <div>
        <BBSHeader title="SIGNAL LOST" />
        <BBSNav />
        <div
          style={{
            textAlign: 'center',
            padding: '40px',
            color: '#ff0040',
          }}
        >
          <div style={{ fontSize: '1.5rem', marginBottom: '10px' }}>
            ▓▓▓ TRANSMISSION NOT FOUND ▓▓▓
          </div>
          <div style={{ color: '#555' }}>
            The requested transcript could not be located in the
            archive.
          </div>
          <button
            className="bbs-button"
            onClick={() => navigate('/archives')}
            style={{ marginTop: '20px' }}
          >
            RETURN TO ARCHIVE
          </button>
        </div>
        <StatusBar />
      </div>
    );
  }

  const htmlContent = DOMPurify.sanitize(
    marked.parse(transcript.content)
  );

  return (
    <div>
      <BBSHeader />
      <BBSNav />

      <button
        className="bbs-back"
        onClick={() => navigate('/archives')}
      >
        {'<'} BACK TO ARCHIVE
      </button>

      {/* CHANGED: <article> wrapper for semantic HTML */}
      <article className="bbs-story-full">
        {/* CHANGED: <h1> instead of generic div for the title */}
        <h1 className="story-title">{transcript.title}</h1>
        <div className="story-meta">
          {/* CHANGED: <time> element with machine-readable datetime */}
          <time
            dateTime={
              transcript.publishedAt || transcript.createdAt
            }
          >
            {formatDate(transcript.createdAt)}
          </time>
          {transcript.tags.length > 0 && (
            <span>
              {' · '}
              {transcript.tags.map(t => `[${t}]`).join(' ')}
            </span>
          )}
        </div>

        <hr className="bbs-divider-double" />

        <div
          className="story-body"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />

        <hr className="bbs-divider-double" />

        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          {transcript.substackUrl && (
            <a
              href={transcript.substackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bbs-button"
              style={{
                textDecoration: 'none',
                marginRight: '10px',
              }}
            >
              READ ON SUBSTACK
            </a>
          )}
          <button
            className="bbs-button"
            onClick={() => navigate('/archives')}
          >
            BACK TO ARCHIVE
          </button>
        </div>

        <div
          style={{
            textAlign: 'center',
            color: '#333',
            fontSize: '0.8rem',
            marginTop: '20px',
          }}
        >
          ═══════════════════════════════════════
          <br />
          END OF TRANSMISSION
          <br />
          ═══════════════════════════════════════
        </div>
      </article>

      <StatusBar />
    </div>
  );
}